import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { trackHeroVideo } from "../../../common/analytics";

// Starts muted, once, with controls. React does not render the muted attribute
// into server-rendered HTML, so it is set on the element before play(). If the
// browser refuses autoplay, or the visitor prefers reduced motion, the poster
// stays and the visitor starts the film with the controls.
const HeroVideo = ({ src, poster, captions, label, describedBy }) => {
  const ref = useRef(null);
  const view = useRef({ active: false, auto: false, sent: {} });

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    video.defaultMuted = true;
    video.muted = true;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    view.current.auto = true;
    const started = video.play();
    if (started && started.catch)
      started.catch(() => {
        view.current.auto = false;
      });
  }, []);

  // One viewing = first play until ended; pause, resume and seek do not send
  // again, each threshold goes out once, and play after ended starts anew.
  const track = (name, params) => trackHeroVideo(name, params, src);

  const onPlay = () => {
    const v = view.current;
    if (v.active) return;
    v.active = true;
    v.sent = {};
    // The film starts by itself, muted: tell that apart from a click.
    track("hero_video_play", { trigger: v.auto ? "autoplay" : "user" });
    v.auto = false;
  };

  const onTimeUpdate = () => {
    const video = ref.current;
    const v = view.current;
    if (!v.active || !video || !video.duration) return;
    const percent = (video.currentTime / video.duration) * 100;
    [25, 50, 75].forEach((mark) => {
      if (percent >= mark && !v.sent[mark]) {
        v.sent[mark] = true;
        track("hero_video_progress", { video_percent: mark });
      }
    });
  };

  const onEnded = () => {
    if (!view.current.active) return;
    view.current.active = false;
    track("hero_video_complete");
  };

  return (
    <video
      ref={ref}
      controls
      playsInline
      preload="metadata"
      poster={poster}
      aria-label={label}
      aria-describedby={describedBy}
      onPlay={onPlay}
      onTimeUpdate={onTimeUpdate}
      onEnded={onEnded}
    >
      <source src={src} type="video/mp4" />
      {captions && (
        <track kind="captions" srcLang="en" label="English" src={captions} />
      )}
    </video>
  );
};

HeroVideo.propTypes = {
  src: PropTypes.string.isRequired,
  poster: PropTypes.string,
  captions: PropTypes.string,
  label: PropTypes.string.isRequired,
  describedBy: PropTypes.string,
};

export default HeroVideo;
